/**
 * components/toastConfig.tsx
 * Premium dark-theme renderers for react-native-toast-message. A card with an
 * accent rail + icon tile that matches the app, lifted above modals on web.
 */
import React from 'react';
import { View, Text, Platform } from 'react-native';
import type { ToastConfig, ToastConfigParams } from 'react-native-toast-message';
import { useTheme } from '../context/themeContext';
import { Icon } from './ui';

const isWeb = Platform.OS === 'web';

function ToastItem({ tone, text1, text2 }: { tone: 'success' | 'error'; text1?: string; text2?: string }) {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const t = tone === 'success'
    ? { fg: colors.success, soft: colors.successSoft, line: colors.successLine, icon: 'check-circle' }
    : { fg: colors.danger, soft: colors.dangerSoft, line: colors.dangerLine, icon: 'alert-circle' };
  // Surface + shadow are tuned per theme: light mode gets a bright card with a
  // soft, subtle ink shadow; dark mode gets a lifted card with a crisper border
  // and a deeper shadow so it reads clearly against the near-black canvas.
  const webShadow = isDark
    ? '0 18px 44px -12px rgba(0,0,0,0.65), 0 2px 10px rgba(0,0,0,0.45)'
    : '0 14px 34px -12px rgba(16,24,40,0.20), 0 2px 8px rgba(16,24,40,0.08)';
  return (
    <View
      style={{
        flexDirection: 'row', alignItems: 'flex-start', gap: 12, width: '92%', maxWidth: 440,
        paddingVertical: 13, paddingHorizontal: 14, borderRadius: 14, overflow: 'hidden',
        backgroundColor: colors.card, borderWidth: 1, borderColor: isDark ? colors.lineStrong : colors.line,
        ...(isWeb
          ? ({ boxShadow: webShadow, zIndex: 999999 } as any)
          : { elevation: 14, shadowColor: '#000', shadowOpacity: isDark ? 0.4 : 0.14, shadowRadius: 16, shadowOffset: { width: 0, height: 8 } }),
      }}
    >
      {/* accent rail */}
      <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: t.fg }} />
      <View style={{ width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: t.soft, borderWidth: 1, borderColor: t.line, marginLeft: 2 }}>
        <Icon name={t.icon} size={16} color={t.fg} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        {!!text1 && <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.1 }}>{text1}</Text>}
        {!!text2 && <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 2, lineHeight: fontSize.sm * 1.45 }}>{text2}</Text>}
      </View>
    </View>
  );
}

export const toastConfig: ToastConfig = {
  success: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="success" text1={text1} text2={text2} />,
  error: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="error" text1={text1} text2={text2} />,
};
